'use client'

import { useState } from 'react'

import { useRouter, useSearchParams } from 'next/navigation'

import { Checkbox } from '@/app/components/atoms/forms/Checkbox'
import type { RangeSliderValue } from '@/app/components/shared/forms/RangeSliderField'
import { ListingFilters, toSelectOptions } from '@/app/components/shared/ListingFilters'
import { useOptionalPageAnalytics } from '@/app/context/analytics'
import { FILTERED_LISTING_MODULE_TYPE } from '@/app/lib/analytics/constants'
import {
  countActiveFilters,
  formatPriceFilterValue,
  type ActiveFilterState,
} from '@/app/lib/analytics/listingFilterTracking'
import type { SanitizedAppliedFilters } from '@/app/lib/api/sanitizeAppliedFilters'
import type { SubcategoryFilters as SubcategoryFiltersData } from '@/app/lib/api/types-api'
import { ALL_LOCATIONS, PAGE_PARAM } from '@/app/lib/constants'
import { setManualLocation } from '@/app/lib/geolocation'
import { invalidateResolvedTargetCache } from '@/app/lib/hooks/useGeoLocationSync'
import { useListingFieldFilters } from '@/app/lib/hooks/useListingFieldFilters'
import { routes } from '@/app/lib/routing'

import { ANALYTICS_MODULE_NAME } from '@/app/entradas/(general-web)/[slug]/[slug2]/[slug3]/_components/analyticsConstants'
import { ANALYTICS_PAGE_TYPE } from '@/app/entradas/(general-web)/[slug]/[slug2]/[slug3]/_components/buildSubcategoriaAnalyticsContext'
import { READABLE_FILTER_PARAMS } from '@/app/entradas/(general-web)/[slug]/[slug2]/[slug3]/_components/parseSubcategoriaFilters'

import { FIELD_ORDER, buildApplyHref, buildProvinceHref } from './subcategoriaFilterHrefs'

interface SubcategoriaFiltersProps {
  readonly categoria: string
  readonly subcategoria: string
  /** Provincia actual (segmento `[location]`); fuente de verdad del filtro de provincia (FR-018). */
  readonly location: string
  readonly filters: SubcategoryFiltersData
  /** Selección activa ya validada contra `filters` (para Ordenar y Fecha). */
  readonly appliedFilters: SanitizedAppliedFilters
  /** Total de eventos de la consulta actual (`meta.total`), mostrado en "Aplicar filtros (N)". */
  readonly totalResults: number
}

/**
 * Resuelve qué extremos del rango de Precio viajan a la URL legible: si el
 * rango seleccionado coincide con el rango COMPLETO de `price_range` (el
 * usuario no ha tocado el slider), se omiten los dos — equivalente a "sin
 * filtro". Mismo criterio que `PromocionesFilters`/`LoMasVendidoFilters`.
 */
function resolvePriceParams(
  range: RangeSliderValue,
  bounds: RangeSliderValue,
): { min: number | undefined; max: number | undefined } {
  const isFullRange = range.min === bounds.min && range.max === bounds.max
  return isFullRange ? { min: undefined, max: undefined } : { min: range.min, max: range.max }
}

/**
 * `SubcategoriaFilters` — envoltorio fino sobre `ListingFilters` (DS compartido).
 * A diferencia de lo-más-vendido, la Provincia NO es el `instantField` sino un
 * `field` ordenado: `ListingFilters` renderiza `instantField` siempre primero, y el
 * orden exigido aquí es Ordenar → Provincia (FR-012), con Provincia en medio. En
 * desktop cada `field` aplica al instante vía su propio `onChange` (Provincia
 * navega el segmento `[location]`, Ordenar reescribe query params); en el
 * SideSheet móvil los dos se acumulan en el borrador y se aplican juntos con
 * "Aplicar filtros (N)" (`onApply` compone ruta + query en un solo push).
 *
 * Fecha se maqueta como chips de selección única (`dateField`), aplicación
 * instantánea, siempre fuera del borrador.
 *
 * Precio (revisado — mismo mecanismo que `PromocionesFilters`/
 * `LoMasVendidoFilters`, unifica la maquetación del selector de precio en
 * todas las PLP del sitio salvo `/search`) pasó de un `min_price` de tramo
 * único (`fields[]`) a un rango CONTINUO acotado por `filters.price_range`,
 * modelado como el `rangeField` de `ListingFilters` (slider de doble asa), de
 * aplicación instantánea al soltar el slider, fuera del borrador. Junto al
 * slider, un checkbox "Mostrar solo gratuitos" (`extra`) — visible solo si la
 * clave `filters.free_filter` está presente en la respuesta — navega con
 * `free_filter=on` y deshabilita el slider (`rangeField.sliderDisabled`, el
 * propio `extra` sigue alcanzable para poder desmarcarlo).
 */
export function SubcategoriaFilters({
  categoria,
  subcategoria,
  location,
  filters,
  appliedFilters,
  totalResults,
}: SubcategoriaFiltersProps) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const analytics = useOptionalPageAnalytics()
  const { values, setFilter, setFilters } = useListingFieldFilters({
    paramNames: READABLE_FILTER_PARAMS,
    pageParam: PAGE_PARAM,
  })
  // Recuerda el rango previo a marcar "Mostrar solo gratuitos", para
  // restaurarlo al desmarcar — mismo patrón que `PromocionesFilters`.
  const [rangeBeforeFreeOnly, setRangeBeforeFreeOnly] = useState<RangeSliderValue | null>(null)

  const isPriceFilterActive = values.priceMin !== '' || values.priceMax !== ''
  const isFreeFilterActive = values.freeFilter === 'on'
  const showFreeFilterCheckbox = Boolean(filters.free_filter)
  // Mismo criterio que `PromocionesFilters`: los límites del slider se
  // congelan en cuanto el filtro de precio se aplica. `isFreeFilterActive`
  // cuenta igual que `isPriceFilterActive`: con "Mostrar solo gratuitos"
  // activo el backend acota `price_range` a `[0, 0]`, y dejarlo pasar clava
  // los límites reales del slider en cero (bug detectado 2026-09-09: los
  // thumbs se juntan al desmarcar y no se pueden separar).
  const [stableBounds, setStableBounds] = useState<RangeSliderValue>(filters.price_range)
  if (
    !isPriceFilterActive &&
    !isFreeFilterActive &&
    (stableBounds.min !== filters.price_range.min || stableBounds.max !== filters.price_range.max)
  ) {
    setStableBounds(filters.price_range)
  }
  const priceBounds = stableBounds
  // Con "Mostrar solo gratuitos" activo la URL no lleva `price_min`/
  // `price_max` (`resolvePriceParams` los omite al coincidir con el rango
  // completo), solo `free_filter=on` — sin este caso especial,
  // `currentPriceRange` caería al `else` y devolvería `priceBounds` (rango
  // completo) en vez de `[0, 0]`, indistinguible de "sin filtro" para
  // `rangeField` (bug detectado 2026-09-11: ni el chip de filtro aplicado ni
  // el texto del desplegable, "Gratuitos", podían reaccionar).
  const currentPriceRange: RangeSliderValue = isFreeFilterActive
    ? { min: 0, max: 0 }
    : {
        min: values.priceMin !== '' ? Number(values.priceMin) : priceBounds.min,
        max: values.priceMax !== '' ? Number(values.priceMax) : priceBounds.max,
      }

  // ── Analítica (`filter_change`, `Listado filtrado.csv` → «Cambio de filtro»)
  // Cada handler emite ANTES de navegar (la navegación re-dispara `page_view`).
  // Fuera del `PageAnalyticsProvider` `analytics` es `null`: los filtros siguen
  // funcionando sin tracking. Mismo patrón que `PromocionesFilters`.

  /** Estado vigente, base para `filters_active_count`. Esta PLP no ofrece categoría/subcategoría. */
  const currentFilterState: ActiveFilterState = {
    province: location,
    genre: '',
    dateRange: values.dateRange,
    priceActive: isPriceFilterActive || isFreeFilterActive,
  }

  function emitFilterChange(change: {
    type: string
    action: string
    value: string
    previousValue: string
    /** Estado de filtros RESULTANTE del cambio (no el previo). */
    next: ActiveFilterState
  }) {
    analytics?.trackFilterChange({
      filter_action: change.action,
      filter_type: change.type,
      filter_value: change.value,
      filter_previous_value: change.previousValue,
      filters_active_count: String(countActiveFilters(change.next)),
      module_name: ANALYTICS_MODULE_NAME,
      module_type: FILTERED_LISTING_MODULE_TYPE,
    })
  }

  const previousPriceValue = isFreeFilterActive
    ? 'gratis'
    : isPriceFilterActive
      ? formatPriceFilterValue(currentPriceRange)
      : 'not_set'

  // Provincia (segmento de ruta) y Aplicar (ruta + query) delegan la construcción
  // de la URL en helpers puros y testables (ver subcategoriaFilterHrefs.ts).
  async function navigateToProvince(newLocation: string) {
    // `emptyValue: ALL_LOCATIONS` — al limpiar llega `todos`: es `remove`, no `clear_all`.
    const target = newLocation || ALL_LOCATIONS
    emitFilterChange({
      type: 'provincia',
      action: target === ALL_LOCATIONS ? 'remove' : 'apply',
      value: target,
      previousValue: location === ALL_LOCATIONS ? 'not_set' : location,
      next: { ...currentFilterState, province: target },
    })
    // Persiste la elección manual en la cookie `location`
    // (specs/025-geolocalizacion-web-general) — incluido al limpiar a
    // `ALL_LOCATIONS`, para que esa elección se respete también en el resto
    // de páginas con geolocalización (agenda/recintos/lo-más-vendido/home) y
    // no quede tapada por una detección antigua. Nunca bloquea la
    // navegación salvo por el propio write: se espera ANTES de navegar para que
    // `useGeoLocationSync` no relea la cookie anterior y devuelva a la provincia previa.
    await setManualLocation(target)
    invalidateResolvedTargetCache()
    router.push(buildProvinceHref(categoria, subcategoria, newLocation, searchParams.toString()), { scroll: false })
  }

  function handleSortChange(value: string) {
    // El diccionario reserva `filter_action=sort` para TODO cambio de
    // ordenación, incluido limpiarla. No cuenta como filtro activo.
    emitFilterChange({
      type: 'sort',
      action: 'sort',
      value: value || 'not_set',
      previousValue: String(appliedFilters.sort ?? '') || 'not_set',
      next: currentFilterState,
    })
    setFilter('sort', value)
  }

  function handleDateRangeChange(value: string) {
    emitFilterChange({
      type: 'fecha',
      action: value ? 'apply' : 'remove',
      value: value || 'not_set',
      previousValue: String(appliedFilters.date_range ?? '') || 'not_set',
      next: { ...currentFilterState, dateRange: value },
    })
    setFilter('dateRange', value)
  }

  async function handleApply(applied: readonly string[]) {
    // SideSheet móvil: un `filter_change` por campo que REALMENTE cambia, todos
    // con el `filters_active_count` del estado final. Los `onChange` de cada
    // campo NO se disparan en el borrador (solo actualiza `draft`), así que no
    // hay intermedios duplicados.
    const nextSort = applied[FIELD_ORDER.indexOf('sort')] ?? ''
    const nextProvince = applied[FIELD_ORDER.indexOf('province')] || ALL_LOCATIONS
    const next: ActiveFilterState = { ...currentFilterState, province: nextProvince }

    const previousSort = String(appliedFilters.sort ?? '')
    if (nextSort !== previousSort) {
      emitFilterChange({
        type: 'sort',
        action: 'sort',
        value: nextSort || 'not_set',
        previousValue: previousSort || 'not_set',
        next,
      })
    }

    if (nextProvince !== location) {
      emitFilterChange({
        type: 'provincia',
        action: nextProvince === ALL_LOCATIONS ? 'remove' : 'apply',
        value: nextProvince,
        previousValue: location === ALL_LOCATIONS ? 'not_set' : location,
        next,
      })
      // Mismo criterio que `navigateToProvince` — persiste también cuando el
      // cambio de provincia llega desde el borrador del SideSheet móvil.
      await setManualLocation(nextProvince)
      invalidateResolvedTargetCache()
    }
    router.push(buildApplyHref(categoria, subcategoria, applied, searchParams.toString()), { scroll: false })
  }

  function handlePriceChangeEnd(range: RangeSliderValue) {
    const { min, max } = resolvePriceParams(range, priceBounds)
    const isCleared = min === undefined
    emitFilterChange({
      type: 'precio',
      action: isCleared ? 'remove' : 'apply',
      value: isCleared ? 'not_set' : formatPriceFilterValue(range),
      previousValue: previousPriceValue,
      next: { ...currentFilterState, priceActive: !isCleared },
    })
    setFilters({
      priceMin: min !== undefined ? String(min) : '',
      priceMax: max !== undefined ? String(max) : '',
      freeFilter: '',
    })
  }

  function handleFreeFilterChange(checked: boolean) {
    if (checked) {
      setRangeBeforeFreeOnly(currentPriceRange)
      emitFilterChange({
        type: 'precio',
        action: 'apply',
        value: 'gratis',
        previousValue: previousPriceValue,
        next: { ...currentFilterState, priceActive: true },
      })
      setFilters({ priceMin: '', priceMax: '', freeFilter: 'on' })
      return
    }
    const restoredRange = rangeBeforeFreeOnly ?? priceBounds
    const { min, max } = resolvePriceParams(restoredRange, priceBounds)
    const isCleared = min === undefined
    emitFilterChange({
      type: 'precio',
      action: isCleared ? 'remove' : 'apply',
      value: isCleared ? 'not_set' : formatPriceFilterValue(restoredRange),
      previousValue: 'gratis',
      next: { ...currentFilterState, priceActive: !isCleared },
    })
    setFilters({
      priceMin: min !== undefined ? String(min) : '',
      priceMax: max !== undefined ? String(max) : '',
      freeFilter: '',
    })
  }

  function clearPriceRange() {
    // `rangeField.onClear` — lo invoca "Limpiar filtros" del SideSheet (el
    // precio vive fuera del borrador y navega ya), de ahí `clear_all`.
    emitFilterChange({
      type: 'precio',
      action: 'clear_all',
      value: 'not_set',
      previousValue: previousPriceValue,
      next: { ...currentFilterState, priceActive: false },
    })
    setRangeBeforeFreeOnly(null)
    setFilters({ priceMin: '', priceMax: '', freeFilter: '' })
  }

  function handleClearAll() {
    // "Limpiar todo": un `clear_all` por filtro activo (enum `filter_type`
    // cerrado, sin "todos"), todos con `filters_active_count=0`. Mismo criterio
    // (pendiente de confirmar con Analítica) que Promociones/Lo más vendido.
    const cleared: ActiveFilterState = { province: ALL_LOCATIONS, genre: '', dateRange: '', priceActive: false }
    const active: { type: string; previousValue: string }[] = []
    if (location !== ALL_LOCATIONS) active.push({ type: 'provincia', previousValue: location })
    if (values.dateRange) active.push({ type: 'fecha', previousValue: values.dateRange })
    if (isFreeFilterActive || isPriceFilterActive) active.push({ type: 'precio', previousValue: previousPriceValue })
    for (const { type, previousValue } of active) {
      emitFilterChange({ type, action: 'clear_all', value: 'not_set', previousValue, next: cleared })
    }
  }

  return (
    <ListingFilters
      dateField={{
        label: 'Fecha',
        value: String(appliedFilters.date_range ?? ''),
        options: toSelectOptions(filters.date_range.items),
        onChange: handleDateRangeChange,
      }}
      fields={[
        {
          label: 'Ordenar por',
          value: String(appliedFilters.sort ?? ''),
          options: toSelectOptions(filters.sort.items),
          placeholder: 'Ordenar por',
          disabled: filters.sort.items.length === 0,
          // El orden no es un filtro que se "quite": no genera chip.
          chip: false,
          onChange: handleSortChange,
        },
        {
          label: 'Provincia',
          value: location,
          options: toSelectOptions(filters.province.items),
          placeholder: 'España',
          // "Sin filtrar" de provincia es "todos" (ALL_LOCATIONS), no cadena vacía.
          emptyValue: ALL_LOCATIONS,
          disabled: filters.province.items.length === 0,
          onChange: navigateToProvince,
        },
      ]}
      rangeField={{
        label: 'Precio',
        min: priceBounds.min,
        max: priceBounds.max,
        value: currentPriceRange,
        sliderDisabled: isFreeFilterActive,
        onChangeEnd: handlePriceChangeEnd,
        onClear: clearPriceRange,
        extra: showFreeFilterCheckbox
          ? ({ variant }) => (
              <Checkbox
                label="Mostrar solo gratuitos"
                checked={isFreeFilterActive}
                onChange={handleFreeFilterChange}
                inverse={variant === 'inverse'}
              />
            )
          : undefined,
      }}
      totalResults={totalResults}
      clearValues={['', ALL_LOCATIONS]}
      onApply={handleApply}
      clearAllHref={routes.subcategoria(categoria, ALL_LOCATIONS, subcategoria)}
      onClearAll={handleClearAll}
      analytics={{
        moduleName: ANALYTICS_MODULE_NAME,
        moduleType: FILTERED_LISTING_MODULE_TYPE,
        pageType: ANALYTICS_PAGE_TYPE,
      }}
      showViewToggle
    />
  )
}
